/**
 * AK wholesale pricelist module.
 *
 * Shop metafield setup (one-time, Admin):
 *   Settings → Custom data → Shop → Add definition
 *   Namespace: ak | Key: pricelist_csv | Type: File | Storefront: Public read
 *   Settings → General → Metafields → upload June 2026 CSV export
 *
 * Expected CSV columns:
 *   Style No., Description, Fabric, 1-71 pcs., 72-299 pcs., 300+ pcs., Size
 *
 * Custom parser (e.g. Papa Parse): AkPriceList.setParser(fn) before load()
 */
(function () {
  var cache = null;
  var displayRows = null;
  var loadPromise = null;
  var customParser = null;

  function pushDisplayRow(styleNo, description, fabric, tier1, tier72, tier300, size) {
    var style = (styleNo || '').trim();
    if (!style || style.charAt(0) === '*') return;

    if (!displayRows) displayRows = [];

    displayRows.push({
      styleNo: style,
      description: description || '',
      fabric: fabric || '',
      tier1: tier1 != null ? String(tier1) : '',
      tier72: tier72 != null ? String(tier72) : '',
      tier300: tier300 != null ? String(tier300) : '',
      size: size || ''
    });
  }

  function parsePrice(value) {
    if (value == null || value === '') return null;
    var amount = parseFloat(String(value).replace(/[^0-9.]/g, ''));
    return Number.isNaN(amount) ? null : amount;
  }

  function normalizeRow(row) {
    var style = (row.style || '').trim().toUpperCase();
    if (!style || style.charAt(0) === '*') return null;

    var tiers = {};
    if (row.tier1 != null) tiers[1] = row.tier1;
    if (row.tier72 != null) tiers[72] = row.tier72;
    if (row.tier300 != null) tiers[300] = row.tier300;
    if (!Object.keys(tiers).length) return null;

    return {
      description: row.description || '',
      fabric: row.fabric || '',
      size: row.size || '',
      tiers: tiers
    };
  }

  function normalizeRowsFromObjects(rows) {
    displayRows = [];
    var styles = {};

    rows.forEach(function (row) {
      if (!row) return;

      var styleNo = (row['Style No.'] || row.style || '').trim();
      var entry = normalizeRow({
        style: styleNo,
        description: row.Description || row.description || '',
        fabric: row.Fabric || row.fabric || '',
        tier1: parsePrice(row['1-71 pcs.'] != null ? row['1-71 pcs.'] : row.tier1),
        tier72: parsePrice(row['72-299 pcs.'] != null ? row['72-299 pcs.'] : row.tier72),
        tier300: parsePrice(row['300+ pcs.'] != null ? row['300+ pcs.'] : row.tier300),
        size: row.Size || row.size || ''
      });

      if (entry) styles[styleNo.toUpperCase()] = entry;

      pushDisplayRow(
        styleNo,
        row.Description || row.description || '',
        row.Fabric || row.fabric || '',
        row['1-71 pcs.'] != null ? row['1-71 pcs.'] : row.tier1,
        row['72-299 pcs.'] != null ? row['72-299 pcs.'] : row.tier72,
        row['300+ pcs.'] != null ? row['300+ pcs.'] : row.tier300,
        row.Size || row.size || ''
      );
    });

    return styles;
  }

  function parseNativeCsv(text) {
    displayRows = [];
    var lines = text.split(/\r?\n/).filter(function (line) {
      return line.trim() !== '';
    });

    if (!lines.length) return {};

    var headers = lines[0].split(',').map(function (header) {
      return header.trim();
    });

    var columns = {};
    headers.forEach(function (header, index) {
      columns[header] = index;
    });

    var styles = {};

    for (var i = 1; i < lines.length; i++) {
      var cells = lines[i].split(',');

      function getCell(name) {
        var index = columns[name];
        return index != null ? (cells[index] || '').trim() : '';
      }

      var styleNo = getCell('Style No.');
      var entry = normalizeRow({
        style: styleNo,
        description: getCell('Description'),
        fabric: getCell('Fabric'),
        tier1: parsePrice(getCell('1-71 pcs.')),
        tier72: parsePrice(getCell('72-299 pcs.')),
        tier300: parsePrice(getCell('300+ pcs.')),
        size: getCell('Size')
      });

      if (entry) styles[styleNo.toUpperCase()] = entry;

      pushDisplayRow(
        styleNo,
        getCell('Description'),
        getCell('Fabric'),
        getCell('1-71 pcs.'),
        getCell('72-299 pcs.'),
        getCell('300+ pcs.'),
        getCell('Size')
      );
    }

    return styles;
  }

  function resolveTierPrice(tiers, totalQty) {
    if (!tiers) return null;

    var qty = totalQty || 0;
    var thresholds = [300, 72, 1].filter(function (threshold) {
      return tiers[threshold] != null;
    });

    if (!thresholds.length) return null;

    thresholds.sort(function (a, b) {
      return b - a;
    });

    for (var i = 0; i < thresholds.length; i++) {
      if (qty >= thresholds[i]) return tiers[thresholds[i]];
    }

    return tiers[thresholds[thresholds.length - 1]];
  }

  function getStyleCandidates(source) {
    if (!source) return [];

    var text = String(source).toUpperCase();
    var candidates = [];
    var fullMatch = text.match(/\b([A-Z0-9]{2,10}-[A-Z0-9]{2,6})\b/i);
    var fourDigitMatch = text.match(/(\d{4}-[A-Z0-9]{2})/i);

    if (fullMatch) candidates.push(fullMatch[1].toUpperCase());
    if (fourDigitMatch) candidates.push(fourDigitMatch[1].toUpperCase());

    var seen = {};
    return candidates.filter(function (candidate) {
      if (seen[candidate]) return false;
      seen[candidate] = true;
      return true;
    });
  }

  function extractStyleNo(title) {
    var candidates = getStyleCandidates(title);
    return candidates.length ? candidates[0] : null;
  }

  function findPriceForSource(source, totalQty) {
    if (!cache || !source) return null;

    var candidates = getStyleCandidates(source);
    for (var i = 0; i < candidates.length; i++) {
      var row = cache[candidates[i]];
      if (!row) continue;

      if (totalQty == null) {
        return {
          styleNo: candidates[i],
          description: row.description,
          fabric: row.fabric,
          size: row.size,
          tiers: row.tiers
        };
      }

      return resolveTierPrice(row.tiers, totalQty);
    }

    return null;
  }

  function formatMoney(amount) {
    if (amount == null) return '';

    var theme = window.theme || {};
    var format = theme.currencyCodeEnabled ? theme.moneyWithCurrencyFormat : theme.moneyFormat;

    if (!format) return '$' + amount.toFixed(2);

    var value = amount.toFixed(2);
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(amount)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(amount)))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, value)
      .replace(/\{\{\s*amount\s*\}\}/g, value);
  }

  window.AkPriceList = {
    setParser: function (fn) {
      customParser = fn;
      this.clear();
    },

    load: function (csvUrl) {
      if (!csvUrl) return Promise.resolve(null);
      if (cache) return Promise.resolve(cache);
      if (loadPromise) return loadPromise;

      loadPromise = fetch(csvUrl, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('Pricelist fetch failed');
          return response.text();
        })
        .then(function (text) {
          var parser = customParser || parseNativeCsv;
          if (!customParser) displayRows = [];
          cache = parser(text);
          if (displayRows == null) displayRows = [];
          window.debugLog('[AkPriceList] loaded', {
            styles: cache,
            rows: displayRows
          });
          return cache;
        })
        .catch(function () {
          cache = null;
          displayRows = null;
          loadPromise = null;
          return null;
        });

      return loadPromise;
    },

    getAllRows: function () {
      return displayRows ? displayRows.slice() : [];
    },

    getPrice: function (styleNo, totalQty) {
      if (!cache || !styleNo) return null;
      var key = styleNo.toUpperCase();
      var row = cache[key];
      if (!row) return null;

      if (totalQty == null) {
        return {
          styleNo: key,
          description: row.description,
          fabric: row.fabric,
          size: row.size,
          tiers: row.tiers
        };
      }

      return resolveTierPrice(row.tiers, totalQty);
    },

    extractStyleNo: extractStyleNo,
    getStyleCandidates: getStyleCandidates,
    findPriceForSource: findPriceForSource,
    formatMoney: formatMoney,
    normalizeRowsFromObjects: normalizeRowsFromObjects,

    isReady: function () {
      return cache != null;
    },

    clear: function () {
      cache = null;
      displayRows = null;
      loadPromise = null;
      this.ready = Promise.resolve(null);
    },

    ready: Promise.resolve(null),

    init: function () {
      var el = document.getElementById('ak-pricelist-url');
      if (!el) {
        this.ready = Promise.resolve(null);
        return;
      }

      try {
        var url = JSON.parse(el.textContent);
        this.ready = this.load(url).then(function (result) {
          if (result) {
            document.dispatchEvent(new CustomEvent('ak-pricelist:ready'));
          }
          return result;
        });
      } catch (error) {
        this.ready = Promise.resolve(null);
      }
    }
  };

  window.AkPriceList.init();
})();
